import { HttpErrorResponse } from '@angular/common/http';

export type ParsedApiError = {
  message: string;
  fieldErrors: Record<string, string>;
};

export const LOAN_APPLICATION_API_FALLBACK_MESSAGE =
  'Something went wrong. Please try again.';

const FALLBACK_MESSAGE = LOAN_APPLICATION_API_FALLBACK_MESSAGE;

/** True when the API indicates the resource is already gone (safe to treat delete as success). */
export function isApiNotFoundError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse && error.status === 404) {
    return true;
  }
  const body = httpErrorBody(error);
  const code = readString(body?.['code']).toUpperCase();
  if (code === 'NOT_FOUND') {
    return true;
  }
  const message = readBodyMessage(body, error).toLowerCase();
  return message.includes('not found');
}

export function parseApiError(
  error: unknown,
  options: {
    fieldCodes?: string[];
    fieldLabels?: Record<string, string>;
  } = {},
): ParsedApiError {
  const fieldCodes = options.fieldCodes ?? [];
  const fieldLabels = options.fieldLabels ?? {};
  const body = httpErrorBody(error);
  const fieldErrors: Record<string, string> = {};

  collectStructuredFieldErrors(body, fieldCodes, fieldErrors);

  const rawMessage = readBodyMessage(body, error);
  const prefixed = matchFieldPrefixedMessage(rawMessage, fieldCodes);
  if (prefixed && !fieldErrors[prefixed.fieldCode]) {
    fieldErrors[prefixed.fieldCode] = capitalize(prefixed.detail);
  }

  const humanized = humanizeMessage(rawMessage, fieldCodes, fieldLabels);
  return {
    message: humanized.length > 0 ? humanized : FALLBACK_MESSAGE,
    fieldErrors,
  };
}

function httpErrorBody(error: unknown): Record<string, unknown> | null {
  if (error instanceof HttpErrorResponse) {
    return normalizeBody(error.error);
  }
  return asRecord(error);
}

function normalizeBody(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return { message: trimmed };
    }
  }
  return asRecord(raw);
}

function readBodyMessage(body: Record<string, unknown> | null, error: unknown): string {
  const fromBody = firstNonEmpty(
    readString(body?.['message']),
    readString(body?.['error']),
    readString(body?.['detail']),
    readString(body?.['title']),
  );
  if (fromBody) {
    return fromBody;
  }
  if (error instanceof Error && !error.message.startsWith('Http failure response')) {
    return error.message;
  }
  return FALLBACK_MESSAGE;
}

function collectStructuredFieldErrors(
  body: Record<string, unknown> | null,
  fieldCodes: string[],
  fieldErrors: Record<string, string>,
): void {
  if (!body) {
    return;
  }

  for (const key of ['errors', 'violations', 'details', 'fieldErrors']) {
    const value = body[key];
    if (Array.isArray(value)) {
      collectFieldErrorsFromList(value, fieldCodes, fieldErrors);
    } else if (value && typeof value === 'object') {
      collectFieldErrorsFromMap(value as Record<string, unknown>, fieldCodes, fieldErrors);
    }
  }
}

function collectFieldErrorsFromList(
  items: unknown[],
  fieldCodes: string[],
  fieldErrors: Record<string, string>,
): void {
  for (const item of items) {
    const record = asRecord(item);
    if (!record) {
      continue;
    }
    addFieldError(
      fieldErrors,
      resolveFieldCode(
        firstNonEmpty(
          readString(record['field']),
          readString(record['fieldName']),
          readString(record['property']),
          readString(record['path']),
        ),
        fieldCodes,
      ),
      firstNonEmpty(
        readString(record['message']),
        readString(record['defaultMessage']),
        readString(record['detail']),
      ),
    );
  }
}

function collectFieldErrorsFromMap(
  map: Record<string, unknown>,
  fieldCodes: string[],
  fieldErrors: Record<string, string>,
): void {
  for (const [rawField, rawMessage] of Object.entries(map)) {
    const message = Array.isArray(rawMessage)
      ? firstNonEmpty(...rawMessage.map((entry) => readString(entry)))
      : readString(rawMessage);
    addFieldError(fieldErrors, resolveFieldCode(rawField, fieldCodes), message);
  }
}

function addFieldError(
  fieldErrors: Record<string, string>,
  fieldCode: string | null,
  message: string,
): void {
  if (fieldCode && message && !fieldErrors[fieldCode]) {
    fieldErrors[fieldCode] = capitalize(message);
  }
}

function matchFieldPrefixedMessage(
  message: string,
  fieldCodes: string[],
): { fieldCode: string; detail: string } | null {
  const sorted = [...fieldCodes].sort((a, b) => b.length - a.length);
  for (const code of sorted) {
    const match = new RegExp(String.raw`^${escapeRegex(code)}\s+(.+)$`, 'i').exec(message);
    if (match?.[1]) {
      return { fieldCode: code, detail: match[1].trim() };
    }
  }
  return null;
}

function humanizeMessage(
  message: string,
  fieldCodes: string[],
  fieldLabels: Record<string, string>,
): string {
  const sorted = [...fieldCodes].sort((a, b) => b.length - a.length);
  for (const code of sorted) {
    const label = fieldLabels[code]?.trim() || splitCamelCase(code);
    const replaced = message.replace(new RegExp(String.raw`\b${escapeRegex(code)}\b`, 'g'), label);
    if (replaced !== message) {
      return capitalize(replaced);
    }
  }
  return capitalize(message);
}

function resolveFieldCode(raw: string, fieldCodes: string[]): string | null {
  if (!raw) {
    return null;
  }
  if (fieldCodes.includes(raw)) {
    return raw;
  }
  const last = raw.split('.').pop() ?? raw;
  if (fieldCodes.includes(last)) {
    return last;
  }
  const lower = last.toLowerCase();
  return fieldCodes.find((code) => code.toLowerCase() === lower) ?? null;
}

function splitCamelCase(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function capitalize(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function firstNonEmpty(...values: string[]): string {
  return values.find((value) => !!value) ?? '';
}
